import {
  useContext,
  useState,
  useEffect,
  useRef,
  type ChangeEvent,
  type MouseEvent,
  type CSSProperties,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import { Checkbox } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import JoinDisasterSelect from "./JoinDisasterSelect";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../components/SnackBarCustom";
import PopoverCustom from "../../../../components/PopoverCustom";
import { TokenContext } from "../../../../TokenContext";
import { ManualCreateDisasterRole } from "../../../../role";
import OtherDisasterPopup from "./OtherDisasterPopup";
import {
  getDisasterStatuses,
  joinDisaster,
  splitDisaster,
  deleteDisaster,
} from "../../../../api";
import type {
  DisasterStatusItem,
  DisasterSelectValue,
  SsListSearchContents,
} from "../../../../types/disaster";

type SearchResultProps = {
  searchContent: SsListSearchContents;
};

type SsListColumnId =
  | "CheckBox"
  | "display_code"
  | "name"
  | "operator"
  | "address"
  | "holiday"
  | "damage_at"
  | "is_damage"
  | "description"
  | "detect_at"
  | "reportButton";

type SsListColumn = {
  id: SsListColumnId;
  label: string;
  minWidth: string;
  align?: "left" | "right" | "center";
  CheckBox?: boolean;
  isSmallScreen: boolean;
  checkBox?: boolean;
  changeColor?: boolean;
  site?: boolean;
  m_customer?: boolean;
  m_branch?: boolean;
  m_division?: boolean;
  disaster_detail?: boolean;
  existence?: boolean;
  textField?: boolean;
  button?: boolean;
  position?: CSSProperties["position"];
  right?: number;
  backgroundColor?: string;
  zIndex?: number;
};

const columns: SsListColumn[] = [
  {
    id: "CheckBox",
    label: "",
    minWidth: "64px",
    CheckBox: true,
    isSmallScreen: true,
    checkBox: true,
  },
  {
    id: "display_code",
    label: "Sコード",
    minWidth: "80px",
    isSmallScreen: true,
    changeColor: true,
    site: true,
  },
  {
    id: "name",
    label: "SS名称",
    minWidth: "112px",
    isSmallScreen: true,
    changeColor: true,
    site: true,
  },
  {
    id: "name",
    label: "得意先",
    minWidth: "96px",
    isSmallScreen: false,
    m_customer: true,
  },
  {
    id: "operator",
    label: "運営店",
    minWidth: "112px",
    isSmallScreen: false,
    site: true,
  },
  {
    id: "address",
    label: "住所",
    minWidth: "144px",
    isSmallScreen: false,
    site: true,
  },
  {
    id: "holiday",
    label: "休業日",
    minWidth: "96px",
    isSmallScreen: false,
    site: true,
  },
  // {
  //   id: "latest_alive",
  //   label: "POS死活",
  //   minWidth: "96px",
  //   isSmallScreen: false,
  //   site: true,
  // },
  // {
  //   id: "latest_sales",
  //   label: "売上有無",
  //   minWidth: "80px",
  //   existence: true,
  //   isSmallScreen: false,
  // },
  {
    id: "damage_at",
    label: "被害報告入力有無",
    minWidth: "96px",
    existence: true,
    isSmallScreen: false,
  },
  {
    id: "is_damage",
    label: "被害有無",
    minWidth: "96px",
    existence: true,
    isSmallScreen: false,
  },
  {
    id: "description",
    label: "検知理由",
    minWidth: "80px",
    disaster_detail: true,
    isSmallScreen: false,
  },
  {
    id: "detect_at",
    label: "検知日時",
    minWidth: "80px",
    isSmallScreen: false,
  },
  {
    id: "reportButton",
    label: "",
    minWidth: "180px",
    button: true,
    align: "right",
    isSmallScreen: true,
    position: "sticky",
    right: 0,
    backgroundColor: "#fff",
    zIndex: 1,
  },
];

const textFieldValidate = (input: boolean | undefined) =>
  input ? <TextField size="small" /> : null;

const existenceValidate = (
  disasterStatus: DisasterStatusItem,
  column: string
) => {
  let status = false;
  if (column === "is_damage") {
    status = Boolean(
      disasterStatus.personal_damage ||
        disasterStatus.property_damage ||
        disasterStatus.facility_damage
    );
  } else {
    status = disasterStatus[column as keyof DisasterStatusItem] != null;
  }
  return status ? (
    <Box>
      <PanoramaFishEyeIcon />
    </Box>
  ) : (
    <Box>
      <CloseIcon />
    </Box>
  );
};

const getSiteField = (
  disasterStatus: DisasterStatusItem,
  column: SsListColumn
): string | number | undefined => {
  const site = disasterStatus.site;
  if (!site) {
    return undefined;
  }
  if (column.site) {
    return site[column.id as keyof typeof site] as string | number | undefined;
  }
  if (column.m_branch) {
    return site.m_branch?.[column.id as keyof NonNullable<typeof site.m_branch>] as
      | string
      | number
      | undefined;
  }
  if (column.m_customer) {
    return site.m_customer?.[column.id as keyof NonNullable<typeof site.m_customer>] as
      | string
      | number
      | undefined;
  }
  if (column.m_division) {
    return site.m_division?.[column.id as keyof NonNullable<typeof site.m_division>] as
      | string
      | number
      | undefined;
  }
  if (column.disaster_detail) {
    return disasterStatus.disaster_detail?.[
      column.id as keyof NonNullable<typeof disasterStatus.disaster_detail>
    ] as string | number | undefined;
  }
  return disasterStatus[column.id as keyof DisasterStatusItem] as
    | string
    | number
    | undefined;
};

const SearchResult = ({ searchContent }: SearchResultProps) => {
  const tokenContext = useContext(TokenContext);
  const user = tokenContext?.user;
  const manualCreateDisasterRole = ManualCreateDisasterRole(
    user?.m_organization.m_role_id ?? 0
  );
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [disasterStatuses, setDisasterStatuses] = useState<DisasterStatusItem[]>(
    []
  );
  const [totalData, setTotalData] = useState(0);

  const lastSearchedContentRef = useRef<SsListSearchContents | null>(null);

  const navigate = useNavigate();
  //復旧報告画面へ遷移
  const handleClick = (event: MouseEvent<HTMLElement>, path: string) => {
    event.stopPropagation();
    navigate(path, { state: { previousPage: "sslist" } });
  };

  const getDisasterSsData = async (overridePage?: number) => {
    if (!disasterId) {
      return;
    }
    const pageToUse = overridePage !== undefined ? overridePage : page;
    // 検索ボタン押下時は現在の検索条件、ページネーション時は最後に確定した条件を使用
    const contentToUse =
      overridePage !== undefined
        ? searchContent
        : (lastSearchedContentRef.current ?? searchContent);
    try {
      const response = await getDisasterStatuses(disasterId, {
        display_code: contentToUse.display_code,
        name: null,
        pref: contentToUse.pref,
        m_branch_id: contentToUse.m_branch_id,
        m_customer_id: contentToUse.m_customer_id,
        latest_alive: contentToUse.latest_alive,
        latest_sales: contentToUse.latest_sales,
        latest_authori: contentToUse.latest_authori,
        is_input: contentToUse.is_input,
        is_damage: contentToUse.is_damage,
        order: null,
        sort: null,
        page: pageToUse + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        return;
      }
      const data = response.disaster_statuses;
      const total = response.total_count ?? 0;
      const status: SnackbarData = response;
      // テナント情報をステートにセット
      setDisasterStatuses(data);
      setTotalData(total);
      // 検索ボタンで1ページ目を指定した場合はページ状態を同期し、確定条件を更新
      if (overridePage !== undefined) {
        setPage(overridePage);
        lastSearchedContentRef.current = searchContent;
      } else {
        // 初回またはページネーション時は今回使用した条件を保持
        lastSearchedContentRef.current = contentToUse;
      }
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
      // ダッシュボードに戻る
      // navigate("/dashboard");
    }
  };

  //スナックバー管理
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  type ButtonValidateProps = {
    button?: boolean;
    statusId?: number;
    otherDisasterStatuses?: DisasterStatusItem["other_disaster_statuses"];
  };

  const ButtonVaridate = ({
    button,
    statusId,
    otherDisasterStatuses,
  }: ButtonValidateProps) => {
    return button ? (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-end" }}>

        <Button
          name="reportDamageButton"
          variant="contained"
          onClick={(event) =>
            handleClick(
              event,
              `/dashboard/situationcheck/report?disaster-id=${disasterId}&status-id=${statusId}`
            )
          }
          sx={{ pointerEvents: "auto" }}
        >
          被害状況報告
        </Button>
        <OtherDisasterPopup otherDisasterStatuses={otherDisasterStatuses} />
      </Box>
    ) : null;
  };

  //初回起動時の読み込み回避
  const isFirstRender = useRef(false);

  useEffect(() => {
    // このeffectは初回レンダー時のみ呼ばれるeffect
    isFirstRender.current = true;
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      // 初回レンダー判定
      isFirstRender.current = false; // もう初回レンダーじゃないよ代入
      return; // 初回レンダー時はAPI呼び出しをスキップ
    }
    getDisasterSsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const [selectedRow, setSelectedRow] = useState<DisasterStatusItem | null>(
    null
  );
  //*ポップアップ設定 */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  //チェックボックスのチェック管理
  const [checked, setChecked] = useState<number[]>([]);
  const [checkedName, setCheckedName] = useState<string[]>([]);

  type CheckboxValidateProps = {
    siteId: number;
    siteName: string;
  };

  const CheckboxValidate = ({ siteId, siteName }: CheckboxValidateProps) => {
    //checkedにsiteIdが存在する場合、isCheckedがtrue
    const isChecked = checked.includes(siteId);
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked === true) {
        setChecked((prevChecked) => {
          if (!prevChecked.includes(siteId)) {
            return [...prevChecked, siteId];
          }
          return prevChecked;
        });
        setCheckedName((prevChecked) => {
          if (!prevChecked.includes(siteName)) {
            return [...prevChecked, siteName];
          }
          return prevChecked;
        });
      } else {
        // チェックが外された場合、siteId を checked 配列から削除
        setChecked((prevChecked) =>
          prevChecked.filter((checkedId) => checkedId !== siteId)
        );
        setCheckedName((prevChecked) =>
          prevChecked.filter((checkedId) => checkedId !== siteName)
        );
      }
    };

    return (
      <Checkbox
        name="checkbox"
        size="small"
        checked={isChecked}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleChange(e)}
        sx={{ cursor: "pointer", pointerEvents: "auto" }}
      />
    );
  };

  const handleLocationCodeClick = (row: DisasterStatusItem) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  //検索結果ページネーション
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //選択した災害名称管理
  const [DisasterName, setDisasterName] = useState<string>();
  const handleChangeDisaster = (e: DisasterSelectValue) => {
    setSelectedContents((prev) => ({
      ...prev,
      dst_id: e.id ?? null,
    }));
    setDisasterName(e.name);
  };

  //結合確認コメント
  var joinMessage = (
    <div>
      {checkedName.map((name, index) => (
        <span key={index}>
          ・{name}
          <br />
        </span>
      ))}
      <span>
        を "{DisasterName}"
        <br />
        に結合しますか？
      </span>
    </div>
  );
  //分割確認コメント
  var splitMessage = (
    <div>
      {checkedName.map((name, index) => (
        <span key={index}>
          ・{name}
          <br />
        </span>
      ))}
      を分割しますか？
    </div>
  );
  //削除確認コメント
  var deleteMessage = (
    <div>
      {checkedName.map((name, index) => (
        <span key={index}>
          ・{name}
          <br />
        </span>
      ))}
      を削除しますか？
    </div>
  );

  //災害情報結合
  const [popoverJoinOpen, setPopoverJoinOpen] = useState(false);
  const joinButtonClick = () => {
    setPopoverJoinOpen(true);
  };
  const joinPopupClose = () => {
    setPopoverJoinOpen(false);
  };
  const [selectedContents, setSelectedContents] = useState<{
    dst_id: number | null;
  }>({
    dst_id: null,
  });
  const joinDisasters = async (isSuccess: boolean) => {
    if (!disasterId) {
      return;
    }
    if (!isSuccess) {
      console.log("何も行いません");
      return;
    }

    const params = {
      site_id: checked,
      dst_id: selectedContents.dst_id ?? 0,
    };
    console.log("災害情報結合入力データ", params);
    try {
      const status = await joinDisaster(disasterId, params);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      console.log("災害情報結合:", status);
      //結合後選択済みサイトをリセット
      setChecked([]);
      setCheckedName([]);
      getDisasterSsData();
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  };

  //災害情報分割
  const [popoverSplitOpen, setPopoverSplitOpen] = useState(false);
  const splitButtonClick = () => {
    setPopoverSplitOpen(true);
  };
  const splitPopupClose = () => {
    setPopoverSplitOpen(false);
  };
  const splitDisasters = async (isSuccess: boolean) => {
    if (!isSuccess || !disasterId) {
      console.log("何も行いません");
      return;
    }

    const params = {
      site_id: checked,
    };
    console.log("災害情報分割入力データ", params);
    try {
      const status = await splitDisaster(disasterId, params);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      console.log("災害情報分割:", status);
      //結合後選択済みサイトをリセット
      setChecked([]);
      setCheckedName([]);
      getDisasterSsData();
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  };

  //災害情報削除
  const [popoverDeleteOpen, setPopoverDeleteOpen] = useState(false);
  const deleteButtonClick = () => {
    setPopoverDeleteOpen(true);
  };
  const deletePopupClose = () => {
    setPopoverDeleteOpen(false);
  };

  const deleteDisasters = async (isSuccess: boolean) => {
    if (!isSuccess || !disasterId) {
      console.log("何も行いません");
      return;
    }

    console.log("災害情報削除入力データ", { site_id: checked });
    try {
      const status = await deleteDisaster(disasterId, { site_id: checked });
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      console.log("災害情報削除:", status);
      //結合後選択済みサイトをリセット
      setChecked([]);
      setCheckedName([]);
      getDisasterSsData();
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          padding: "16px 0 32px",
          "@media screen and (max-width:600px)": {
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        <Button
          name="searchButton"
          variant="contained"
          onClick={() => {
            setPage(0);
            getDisasterSsData(0);
          }}
          sx={{ width: "208px" }}
        >
          検索
        </Button>
      </Box>
      <Divider />
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            gap: "16px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              paddingLeft: "40px",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            検索結果
          </Box>
          {/* <Button onClick={checkConsole}>コンソール確認</Button> */}
        </Box>
        <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
          <TableContainer>
            <Table
              stickyHeader
              aria-label="sticky table"
              sx={{ width: "100%" }}
            >
              <TableHead>
                <TableRow sx={{ overflowX: "auto" }}>
                  {columns.map((column) => (
                    <TableCell
                      key={`${column.label}_${column.id}`}
                      align={column.align}
                      style={{
                        minWidth: column.minWidth,
                        position: column.position,
                        right: column.right,
                      }}
                      sx={{
                        padding: "8px",
                        "@media screen and (max-width:900px)": {
                          display:
                            column.isSmallScreen === true
                              ? "table-cell"
                              : "none",
                        },
                        backgroundColor: column.changeColor
                          ? "primary.light"
                          : "#FFF",
                        color: column.changeColor ? "#FFF" : "outline.dark",
                        fontWeight: "bold",
                        borderBottom: "2px solid #606060",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {disasterStatuses?.map((disasterStatus, rowNum) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={`${disasterStatus.id}_${rowNum}`}
                      onClick={() => handleLocationCodeClick(disasterStatus)}
                      sx={{
                        pointerEvents: "none",
                        "@media screen and (max-width:900px)": {
                          cursor: "pointer",
                          pointerEvents: "auto",
                        },
                      }}
                    >
                      {columns.map((column) => {
                        return (
                          <TableCell
                            key={`${column.label}_${column.id}`}
                            align={column.align}
                            style={{
                              minWidth: column.minWidth,
                              position: column.position,
                              right: column.right,
                              backgroundColor: column.backgroundColor,
                            }}
                            sx={{
                              padding: "8px",
                              "@media screen and (max-width:900px)": {
                                display:
                                  column.isSmallScreen === true
                                    ? "table-cell"
                                    : "none",
                              },
                            }}
                          >
                            {column.existence ? (
                              <>
                                {existenceValidate(disasterStatus, column.id)}
                              </>
                            ) : column.site ||
                              column.m_branch ||
                              column.m_customer ||
                              column.m_division ||
                              column.disaster_detail ? (
                              <>{getSiteField(disasterStatus, column)}</>
                            ) : (
                              <>{getSiteField(disasterStatus, column)}</>
                            )}
                            {column.checkBox && disasterStatus.site ? (
                              <CheckboxValidate
                                siteId={disasterStatus.site.id}
                                siteName={String(disasterStatus.site.name ?? "")}
                              />
                            ) : null}
                            {textFieldValidate(column.textField)}
                            <ButtonVaridate
                              button={column.button}
                              statusId={disasterStatus["id"]}
                              otherDisasterStatuses={
                                disasterStatus["other_disaster_statuses"]
                              }
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {manualCreateDisasterRole.update ? (
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                padding: "8px",
              }}
            >
              <JoinDisasterSelect
                changedValue={(value) => handleChangeDisaster(value)}
                ownId={disasterId}
              />
              <Button
                name="joinButton"
                variant="contained"
                onClick={joinButtonClick}
                disabled={
                  checked.length === 0 || selectedContents.dst_id === null
                }
              >
                結合
              </Button>
              <Button
                name="splitButton"
                variant="outlined"
                onClick={splitButtonClick}
                disabled={checked.length === 0}
              >
                分割
              </Button>
              <IconButton
                name="deleteButton"
                color="error"
                onClick={deleteButtonClick}
                disabled={checked.length === 0}
              >
                <DeleteForeverOutlinedIcon />
              </IconButton>
            </Box>
          ) : null}
          <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
            <Box
              sx={{
                display: "flex",
                height: "56px",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                borderBottom: "2px solid #CCC",
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  paddingLeft: "8px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                対象SS詳細
              </Box>
              <IconButton
                aria-label="close"
                onClick={handlePopoverClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
              <TableBody>
                {selectedRow &&
                  columns.slice(1, 10).map((column) => {
                    // const value = selectedRow[column.id];
                    return (
                      <TableRow role="checkbox" tabIndex={-1}>
                        <TableCell
                          align={column.align}
                          sx={{
                            color: "outline.dark",
                            fontWeight: "bold",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              WebkitWritingMode: "horizontal-tb",
                              height: "100%",
                            }}
                          >
                            {column.label}
                          </Box>
                        </TableCell>
                        <TableCell
                          align={column.align}
                          sx={{
                            writingMode: "horizontal-tb",
                            boxSizing: "border-box",
                            width: "calc((100% - 64px) / 2)",
                            overflowWrap: "break-word",
                          }}
                        >
                          {column.existence ? (
                            <>{existenceValidate(selectedRow, column.id)}</>
                          ) : (
                            <>{getSiteField(selectedRow, column)}</>
                          )}

                          {column.checkBox && selectedRow.site ? (
                            <CheckboxValidate
                              siteId={selectedRow.site.id}
                              siteName={String(selectedRow.site.name ?? "")}
                            />
                          ) : null}
                          {textFieldValidate(column.textField)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <Box
              sx={{
                margin: "16px",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  padding: "0 8px",
                  height: "36.5px",
                  border: "solid 1px #0752A3",
                  borderRadius: "4px",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  lineHeight: "1.75",
                  letterSpacing: "0.02857",
                  color: "#0752A3",
                }}
              >
                選択
                {selectedRow?.site ? (
                  <CheckboxValidate
                    siteId={selectedRow.site.id}
                    siteName={String(selectedRow.site.name ?? "")}
                  />
                ) : null}
              </Box>
              <ButtonVaridate button={true} statusId={selectedRow?.["id"]} />
            </Box>
          </Dialog>
          <PopoverCustom
            open={popoverJoinOpen}
            onClose={joinPopupClose}
            yesNo={joinDisasters}
            title={"結合確認"}
            contents={joinMessage}
          />
          <PopoverCustom
            open={popoverSplitOpen}
            onClose={splitPopupClose}
            yesNo={splitDisasters}
            title={"分割確認"}
            contents={splitMessage}
          />
          <PopoverCustom
            open={popoverDeleteOpen}
            onClose={deletePopupClose}
            yesNo={deleteDisasters}
            title={"削除確認"}
            contents={deleteMessage}
          />
          <SnackbarCustom
            data={snackBarstatus}
            openStatus={snackbarOpen}
            onClose={() => setSnackbarOpen(false)}
          />
          <TablePagination
            rowsPerPageOptions={[10, 30, 50, 100]}
            component="div"
            count={totalData}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
              {
                margin: "0 !important",
                padding: "0 !important",
              },
            }}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
          />
        </Paper>
      </Box>
    </>
  );
};
export default SearchResult;
